import type { Node, Viewport } from "@xyflow/react";
import { frameNodeSize } from "@/lib/frame-node-geometry";

type ViewportNode = Pick<Node, "position"> &
  Partial<
    Pick<Node, "type" | "data" | "width" | "height" | "style" | "measured">
  >;

interface ViewportInsets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

interface ViewportForNodesOptions {
  viewportWidth: number;
  viewportHeight: number;
  insets: ViewportInsets;
  maxZoom: number;
  paddingRatio: number;
}

export function calculateExtendedDesktopViewport(
  origin: { x: number; y: number },
  zoom: number,
): Viewport {
  return {
    x: origin.x === 0 ? 0 : -origin.x * zoom,
    y: origin.y === 0 ? 0 : -origin.y * zoom,
    zoom,
  };
}

export function calculateViewportForNodes(
  nodes: ViewportNode[],
  options: ViewportForNodesOptions,
): Viewport | null {
  if (nodes.length === 0) return null;

  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;

  for (const node of nodes) {
    const { width, height } = frameNodeSize(node);
    minX = Math.min(minX, node.position.x);
    minY = Math.min(minY, node.position.y);
    maxX = Math.max(maxX, node.position.x + width);
    maxY = Math.max(maxY, node.position.y + height);
  }

  if (![minX, minY, maxX, maxY].every(Number.isFinite)) return null;

  const availW = Math.max(
    1,
    options.viewportWidth - options.insets.left - options.insets.right,
  );
  const availH = Math.max(
    1,
    options.viewportHeight - options.insets.top - options.insets.bottom,
  );
  const boundsW = Math.max(1, maxX - minX);
  const boundsH = Math.max(1, maxY - minY);
  const paddedW = boundsW * (1 + options.paddingRatio * 2);
  const paddedH = boundsH * (1 + options.paddingRatio * 2);
  const zoom = Math.min(
    Math.max(Math.min(availW / paddedW, availH / paddedH), 0.01),
    options.maxZoom,
  );
  const centerX = minX + boundsW / 2;
  const centerY = minY + boundsH / 2;
  const screenCenterX = options.insets.left + availW / 2;
  const screenCenterY = options.insets.top + availH / 2;

  return {
    x: screenCenterX - centerX * zoom,
    y: screenCenterY - centerY * zoom,
    zoom,
  };
}
